import { useEffect, useRef, useState } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { Alert, Button, Card, CircularProgress, TextField, Typography } from "@mui/material";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import vetService from "@fuse/services/vetService";
import UserVetList from "./UserVetList";
import { Practice, practiceDetails, searchPractices } from "./placesSearch";

function AddVetApp() {
    const [vets, setVets] = useState<any[]>([]);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<Practice[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState("");
    const [error, setError] = useState("");
    const [vetError, setVetError] = useState("");
    const requestId = useRef(0);
    const { placesService } = usePlacesService({ apiKey: process.env.VITE_APP_GOOGLE });
    async function loadVets() {
        try { setVets(await vetService.getUserVets() || []); setVetError(""); }
        catch { setVetError("Unable to load your vets. Please try again."); }
    }
    useEffect(() => { loadVets(); return () => { requestId.current++; }; }, []);
    async function search(event: React.FormEvent) {
        event.preventDefault();
        if (!query.trim()) { setError("Enter a practice name, address or zip code."); return; }
        if (!placesService) { setError("Practice search is unavailable right now. Please try again shortly."); return; }
        const id = ++requestId.current;
        setLoading(true); setError(""); setResults([]); setSearched(false);
        try {
            const found = await searchPractices(placesService, query);
            const detailed = await Promise.all(found.map(p => practiceDetails(placesService, p)));
            if (id === requestId.current) { setResults(detailed); setSearched(true); }
        } catch (e) { if (id === requestId.current) setError(e instanceof Error ? e.message : "Search failed. Please try again."); }
        finally { if (id === requestId.current) setLoading(false); }
    }
    async function add(practice: Practice) {
        setSaving(practice.place_id); setError("");
        try {
            const detail = { name: practice.name, googlePlaceId: practice.place_id, isActive: true,
                address1: practice.formatted_address, phoneNumber: practice.formatted_phone_number, url: practice.website, placeDetail: practice };
            let site = await vetService.getSiteByGooglePlaceId(practice.place_id, { ...detail, placeDetail: null });
            if (!site?.id) site = await vetService.addSite(detail);
            if (!site?.id) throw new Error("Unable to save this practice.");
            await vetService.saveForOwner(site.id);
            await loadVets();
        } catch { setError("Unable to add this vet. Please try again."); }
        finally { setSaving(""); }
    }
    async function remove(id: string) {
        setSaving(id); setVetError("");
        try { await vetService.removeForOwner(id); await loadVets(); }
        catch { setVetError("Unable to remove this vet. Please try again."); }
        finally { setSaving(""); }
    }
    return <div className="w-full p-[20px] flex flex-col gap-[20px]">
        <Card className="p-[28px]">
            <Typography variant="h6">Your Vets</Typography>
            {vetError && <Alert severity="error">{vetError}<Button onClick={loadVets}>Retry</Button></Alert>}
            <UserVetList vets={vets} />
            {vets.map(v => <div key={v.id} className="flex items-center justify-between gap-4 mt-8">
                <Typography>{v.name}</Typography><Button disabled={!!saving} onClick={() => remove(v.id)}>Remove</Button>
            </div>)}
        </Card>
        <Card className="p-[28px]">
            <form onSubmit={search} className="flex flex-col gap-[20px]">
                <Typography variant="h6">Add a Vet</Typography>
                <TextField fullWidth label="Search by Practice Name, Address, or Zip Code" value={query} onChange={e => setQuery(e.target.value)} />
                <CustomPrimaryButton disabled={loading}>Search</CustomPrimaryButton>
            </form>
            <div aria-live="polite" className="mt-12">
                {loading && <div role="status"><CircularProgress size={20} /> Searching practices…</div>}
                {error && <Alert severity="error">{error}</Alert>}
                {searched && !loading && !results.length && <Typography>No practices found. Check the spelling or try a zip code.</Typography>}
            </div>
            {results.map(p => <div key={p.place_id} className="flex justify-between gap-8 border-b py-12">
                <div><Typography fontWeight={600}>{p.name}</Typography><Typography>{p.formatted_address}</Typography>
                    <Typography>{p.formatted_phone_number || "Phone unavailable"}</Typography></div>
                <Button disabled={!!saving || vets.some(v => v.googlePlaceId === p.place_id)} onClick={() => add(p)}>
                    {vets.some(v => v.googlePlaceId === p.place_id) ? "Added" : "Add"}
                </Button>
            </div>)}
        </Card>
    </div>;
}
export default AddVetApp;
